<template>
    <footer class="site panel">
        <div class="f-top">
            <div class="f-brand">
                <a :href="href(homeLink)" :aria-label="translateWord('Página inicial da Builderall')">
                    <img :src="logo" alt="Builderall" width="103" height="26">
                </a>
            </div>

            <nav v-for="col in columns" :key="col.id" class="f-col" :aria-labelledby="col.id">
                <h3 :id="col.id">
                    <button
                        v-if="compact"
                        class="f-acc"
                        type="button"
                        :aria-expanded="open[col.id] ? 'true' : 'false'"
                        :aria-controls="col.id + '-list'"
                        @click="toggle(col.id)"
                    >
                        <span>{{ label(col) }}</span>
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 15.5 5 8.5l1.4-1.4 5.6 5.6 5.6-5.6L19 8.5z"/></svg>
                    </button>
                    <template v-else>{{ label(col) }}</template>
                </h3>
                <ul :id="col.id + '-list'" :hidden="compact && !open[col.id]">
                    <li v-for="(entry, index) in col.links" :key="col.id + '-' + index">
                        <a :href="href(entry)">{{ label(entry) }}</a>
                    </li>
                </ul>
            </nav>
        </div>

        <div class="f-bar">
            <p>{{ translateWord('Copyright © {0} – Este site foi criado com a Builderall.', [year]) }}</p>
            <div class="socials">
                <a v-for="social in socials" :key="social.linkKey" :href="href(social)" :aria-label="social.name">
                    <svg v-if="social.linkKey === 'facebook'" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M20 10C20 4.477 15.523 0 10 0S0 4.477 0 10c0 5.523 4.477 10 10 10 .059 0 .117 0 .176-.004V12.215H8.027V9.711h2.149V7.867c0-2.137 1.304-3.301 3.21-3.301.915 0 1.7.066 1.926.098v2.234H14c-1.035 0-1.238.492-1.238 1.215v1.594h2.48l-.324 2.504h-2.156v7.402C16.941 18.414 20 14.566 20 10z" fill="#2568EF"/></svg>
                    <svg v-else-if="social.linkKey === 'youtube'" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M8.613 11.613 11.387 10 8.613 8.387v3.226z" fill="#2568EF"/><path d="M10 0C4.477 0 0 4.477 0 10s4.477 10 10 10 10-4.477 10-10S15.523 0 10 0zm5.263 11.77a1.99 1.99 0 0 1-1.99 1.991H6.727a1.99 1.99 0 0 1-1.99-1.991V8.23a1.99 1.99 0 0 1 1.99-1.991h6.546a1.99 1.99 0 0 1 1.99 1.991v3.54z" fill="#2568EF"/></svg>
                    <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="1.914" fill="#2568EF"/><path d="M10 0C4.478 0 0 4.478 0 10s4.478 10 10 10 10-4.478 10-10S15.522 0 10 0zm5.708 12.367c-.028.612-.125 1.029-.267 1.394a3.02 3.02 0 0 1-1.68 1.68c-.365.142-.782.239-1.393.267-.613.028-.808.035-2.368.035s-1.755-.007-2.367-.035c-.611-.028-1.029-.125-1.394-.267a3.02 3.02 0 0 1-1.68-1.68c-.142-.365-.239-.782-.267-1.394-.028-.612-.034-.808-.034-2.367s.006-1.755.034-2.367c.028-.612.125-1.029.267-1.394a3.02 3.02 0 0 1 1.394-1.68c.365-.142.783-.239 1.394-.267.612-.028.808-.035 2.367-.035s1.755.007 2.368.035c.611.028 1.028.125 1.393.267a3.02 3.02 0 0 1 1.68 1.68c.142.365.239.782.267 1.394.028.612.035.808.035 2.367s-.007 1.755-.035 2.367z" fill="#2568EF"/><rect x="6.2" y="6.2" width="7.6" height="7.6" rx="2.4" stroke="#2568EF" stroke-width="1.1"/></svg>
                </a>
            </div>
        </div>
    </footer>
</template>

<script>
import { mapGetters } from 'vuex';
import { footerColumns, socials, resolveHref } from './site-chrome/nav';

const homeLink = { linkKey: 'home' };

export default {
    name: 'SiteFooter',
    data() {
        return {
            columns: footerColumns,
            socials,
            homeLink,
            logo: require('../../assets/images/Icon_white.svg'),
            year: new Date().getFullYear(),
            compact: false,
            open: {}
        };
    },
    computed: {
        ...mapGetters(['translateWord', 'getAllParamsUrl', 'linksPage']),
        links() {
            return this.linksPage('plans');
        }
    },
    mounted() {
        this.phone = window.matchMedia('(max-width:640px)');
        this._onPhone = () => {
            this.compact = this.phone.matches;
            this.open = {};
        };
        if (this.phone.addEventListener) this.phone.addEventListener('change', this._onPhone);
        else this.phone.addListener(this._onPhone);
        this.compact = this.phone.matches;
    },
    beforeDestroy() {
        if (!this.phone) return;
        if (this.phone.removeEventListener) this.phone.removeEventListener('change', this._onPhone);
        else this.phone.removeListener(this._onPhone);
    },
    methods: {
        label(entry) {
            const key = entry.label || entry.name;
            if (entry.literal) return entry.name;
            return key ? this.translateWord(key) : '';
        },
        href(entry) {
            return resolveHref(entry, this.links, this.queryParams(), this.$route.path);
        },
        queryParams() {
            const raw = this.getAllParamsUrl(false) || '';
            const search = new URLSearchParams(raw.charAt(0) === '?' ? raw.slice(1) : raw);
            search.delete('period');
            const q = search.toString();
            return q ? `?${q}` : '';
        },
        toggle(id) {
            this.open = { ...this.open, [id]: !this.open[id] };
        }
    }
};
</script>
